import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trophy, Info, Grid3x3, Search, MapPin, Users, ChevronRight, ShieldAlert } from 'lucide-react';
import { api } from './api';
import { coverSrc, Crest } from './Tournaments';
import { SafetyPanel } from './PublicPortal';

const TABS = [
  { id: 'info', label: 'Event Info', icon: <Info size={15} /> },
  { id: 'divisions', label: 'Divisions', icon: <Grid3x3 size={15} /> },
  { id: 'safety', label: 'Safety', icon: <ShieldAlert size={15} /> },
  { id: 'find', label: 'Find my team', icon: <Search size={15} /> },
];

export default function PublicEvent() {
  const { eventId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [tab, setTab] = useState('info');
  const [teams, setTeams] = useState([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    api.get(`/public/events/${eventId}`).then((r) => { setData(r.data); setStatus('ok'); }).catch(() => setStatus('error'));
    api.get(`/public/events/${eventId}/teams`).then((r) => setTeams(r.data)).catch(() => {});
  }, [eventId]);

  if (status === 'loading') return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-gray-500">Loading…</div>;
  if (status === 'error') return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-gray-500">Event not found.</div>;

  const ev = data.event;
  const divisions = data.divisions;
  const filtered = teams.filter((t) => t.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="ttheme min-h-screen bg-[#0A0A0A] text-white font-sans pb-12" data-testid="public-event">
      <div className="max-w-3xl mx-auto px-5">
        {ev.cover_photo ? (
          <div className="mt-5 rounded-2xl overflow-hidden h-44 sm:h-56 relative">
            <img src={coverSrc(ev.cover_photo)} alt={ev.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#F4F6F9] via-[#F4F6F9]/30 to-transparent" />
          </div>
        ) : null}
        <div className="py-7 text-center border-b border-gray-800">
          {!ev.cover_photo && <Trophy className="w-10 h-10 text-[#00BFFF] mx-auto mb-3" />}
          <h1 className="text-3xl font-black">{ev.name}</h1>
          <div className="flex items-center justify-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
            {ev.location && <span className="flex items-center gap-1"><MapPin size={13} />{ev.location}</span>}
            <span className="flex items-center gap-1"><Grid3x3 size={13} />{divisions.length} divisions</span>
            {ev.start_time && <span>Kick-off {ev.start_time}</span>}
          </div>
        </div>

        <div className="flex gap-1 my-4 bg-[#1C1C1E] border border-gray-800 rounded-xl p-1 sticky top-2 z-10">
          {TABS.map((tb) => (
            <button key={tb.id} data-testid={`event-tab-${tb.id}`} onClick={() => setTab(tb.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-bold transition-colors ${tab === tb.id ? 'bg-[#00BFFF] text-black' : 'text-gray-400'}`}>
              {tb.icon}<span className="hidden sm:inline">{tb.label}</span>
            </button>
          ))}
        </div>

        {tab === 'info' && (
          <div className="space-y-4" data-testid="event-info">
            {ev.description ? (
              <div className="bg-white border border-gray-200 rounded-2xl p-5">
                <p className="whitespace-pre-line text-sm text-gray-700 leading-relaxed">{ev.description}</p>
              </div>
            ) : <p className="text-center text-gray-500 py-6 text-sm">No event details added yet.</p>}
            {(ev.images || []).length > 0 && (
              <div className="grid grid-cols-2 gap-3" data-testid="event-images">
                {ev.images.map((img, i) => (
                  <img key={i} src={coverSrc(img)} alt={`Event ${i + 1}`} className="w-full h-40 object-cover rounded-2xl border border-gray-200" />
                ))}
              </div>
            )}
            <button onClick={() => setTab('divisions')} className="w-full bg-[#00BFFF] text-black font-bold py-3.5 rounded-xl">View divisions</button>
          </div>
        )}

        {tab === 'divisions' && (
          <div className="space-y-2" data-testid="event-divisions">
            {divisions.map((d) => (
              <button key={d.id} data-testid={`event-division-${d.id}`} onClick={() => nav(`/live/${d.id}`)}
                className="w-full text-left bg-white border border-gray-200 rounded-2xl p-4 flex items-center justify-between hover:border-[#00BFFF]/60 transition-colors">
                <div>
                  <p className="font-black text-base">{d.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1"><Users size={12} />{d.team_count} teams{d.entry_open ? ' · entries open' : ''}</p>
                </div>
                <ChevronRight size={20} className="text-[#00BFFF]" />
              </button>
            ))}
          </div>
        )}

        {tab === 'safety' && (
          <div data-testid="event-safety">
            <SafetyPanel src={ev} />
          </div>
        )}

        {tab === 'find' && (
          <div className="space-y-3" data-testid="event-find">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input data-testid="find-team-input" autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Type your team name…"
                className="w-full bg-white border border-gray-200 rounded-xl pl-9 pr-3 py-3.5 text-sm" />
            </div>
            {filtered.length === 0 ? <p className="text-center text-gray-500 py-6 text-sm">{teams.length === 0 ? 'No teams added yet.' : 'No team matches — scroll or check spelling.'}</p> :
              <div className="space-y-2">
                {filtered.map((t) => (
                  <button key={t.team_id} data-testid={`find-team-${t.team_id}`} onClick={() => nav(`/live/${t.division_id}`)}
                    className="w-full text-left bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between hover:border-[#00BFFF]/60">
                    <span className="flex items-center gap-3 min-w-0">
                      <Crest name={t.name} logo={t.logo} size={28} />
                      <span className="min-w-0">
                        <span className="font-bold block truncate">{t.name}</span>
                        <span className="text-[11px] text-gray-500">{t.division_label}</span>
                      </span>
                    </span>
                    <ChevronRight size={18} className="text-[#00BFFF] shrink-0" />
                  </button>
                ))}
              </div>}
          </div>
        )}

        <p className="text-center text-[10px] text-gray-600 mt-10 tracking-wider">Powered by Tournevo</p>
      </div>
    </div>
  );
}
